import React from 'react';
import { X } from 'lucide-react';
import { colors } from '../../styles/colors';
import { formatEuro } from '../../utils/formatters';
import ModalPortal from '../ModalPortal';

/**
 * Modal affichant le detail des KPIs facturation par periode
 * - Interne : par trimestre (Q1-Q4)
 * - Externe : par mois (Jan-Dec)
 */
export default function FacturationDetailModal({ isOpen, onClose, type, detailData }) {
  if (!isOpen || !detailData) return null;

  const { periods, totals } = detailData;
  const title = type === 'interne'
    ? 'Detail par trimestre'
    : 'Detail par mois';

  return (
    <ModalPortal>
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '0.75rem',
          border: `2px solid ${colors.primary.teal}`,
          width: '100%',
          maxWidth: type === 'interne' ? '600px' : '650px',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 1.25rem',
          borderBottom: `1px solid ${colors.secondary.lightGray}`,
          backgroundColor: colors.primary.teal,
          borderRadius: '0.6rem 0.6rem 0 0'
        }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: 'white' }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <X size={18} color="white" />
          </button>
        </div>

        {/* Table */}
        <div style={{ padding: '1rem 1.25rem' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.875rem'
          }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${colors.secondary.lightGray}` }}>
                <th style={{
                  padding: '0.5rem 0.75rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: colors.primary.gray,
                  fontSize: '0.8rem'
                }}>
                  {type === 'interne' ? 'Trimestre' : 'Mois'}
                </th>
                <th style={{
                  padding: '0.5rem 0.75rem',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: colors.primary.teal,
                  fontSize: '0.8rem'
                }}>
                  CA Genere
                </th>
                <th style={{
                  padding: '0.5rem 0.75rem',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: colors.text.goldDark || colors.primary.gold,
                  fontSize: '0.8rem'
                }}>
                  Total Facture
                </th>
                <th style={{
                  padding: '0.5rem 0.75rem',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: colors.primary.gray,
                  fontSize: '0.8rem'
                }}>
                  Reste a Facturer
                </th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p, idx) => (
                <tr
                  key={p.label}
                  style={{
                    borderBottom: `1px solid ${colors.secondary.lightGray}`,
                    backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb'
                  }}
                >
                  <td style={{
                    padding: '0.5rem 0.75rem',
                    fontWeight: '500',
                    color: colors.primary.gray
                  }}>
                    {p.label}
                  </td>
                  <td style={{
                    padding: '0.5rem 0.75rem',
                    textAlign: 'right',
                    color: colors.primary.teal
                  }}>
                    {formatEuro(p.ca)}
                  </td>
                  <td style={{
                    padding: '0.5rem 0.75rem',
                    textAlign: 'right',
                    color: colors.text.goldDark || colors.primary.gold
                  }}>
                    {formatEuro(p.facture)}
                  </td>
                  <td style={{
                    padding: '0.5rem 0.75rem',
                    textAlign: 'right',
                    fontWeight: '600',
                    color: p.reste > 0 ? colors.status.error : colors.status.success
                  }}>
                    {formatEuro(Math.abs(p.reste))}
                    {p.reste < 0 && ' (surplus)'}
                  </td>
                </tr>
              ))}

              {/* Total row */}
              <tr style={{
                borderTop: `2px solid ${colors.primary.gray}`,
                backgroundColor: '#f3f4f6'
              }}>
                <td style={{
                  padding: '0.6rem 0.75rem',
                  fontWeight: '700',
                  color: colors.primary.gray
                }}>
                  Total
                </td>
                <td style={{
                  padding: '0.6rem 0.75rem',
                  textAlign: 'right',
                  fontWeight: '700',
                  color: colors.primary.teal
                }}>
                  {formatEuro(totals.ca)}
                </td>
                <td style={{
                  padding: '0.6rem 0.75rem',
                  textAlign: 'right',
                  fontWeight: '700',
                  color: colors.text.goldDark || colors.primary.gold
                }}>
                  {formatEuro(totals.facture)}
                </td>
                <td style={{
                  padding: '0.6rem 0.75rem',
                  textAlign: 'right',
                  fontWeight: '700',
                  color: totals.reste > 0 ? colors.status.error : colors.status.success
                }}>
                  {formatEuro(Math.abs(totals.reste))}
                  {totals.reste < 0 && ' (surplus)'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}
